import {
  DiscriminantStatus,
  MatchQualification,
  ScreeningOutcome,
  type MatchReview,
  type QualifiableMatch,
} from "@/core/compliance/entities/MatchReview";

export interface QualifiableMatchDto {
  matchId: string;
  qualification?: string | null;
  qualifiedBy?: string | null;
  qualifiedAt?: string | null;
  motif?: string | null;
  entityFullName?: string | null;
  entityDateOfBirth?: string | null;
  entityPlaceOfBirth?: string | null;
  entitySourceCountry?: string | null;
  entityCategory?: string | null;
  politicallyExposed?: boolean | null;
  matchScore?: number | null;
  sources?: string[] | null;
  amlTypes?: string[] | null;
  dateOfBirthStatus?: string | null;
  placeOfBirthStatus?: string | null;
}

export interface MatchReviewDto {
  businessKey: string;
  customerFullName?: string | null;
  customerDateOfBirth?: string | null;
  customerNationality?: string | null;
  customerPlaceOfBirth?: string | null;
  matches?: QualifiableMatchDto[] | null;
  outcome?: string | null;
  remainingToQualify?: number | null;
}

/**
 * Un discriminant inconnu est ramené à « indisponible », jamais à « concordante » ni à
 * « différente » : les deux autres valeurs sont des arguments de décision, et en inventer un
 * ferait retenir ou écarter sur du vide. « Indisponible » est le seul repli qui n'affirme rien.
 */
function mapDiscriminant(raw: string | null | undefined): DiscriminantStatus {
  switch (raw) {
    case "CONCORDANTE":
      return DiscriminantStatus.CONCORDANTE;
    case "PARTIELLE":
      return DiscriminantStatus.PARTIELLE;
    case "DIFFERENTE":
      return DiscriminantStatus.DIFFERENTE;
    default:
      return DiscriminantStatus.INDISPONIBLE;
  }
}

/** `null` signifie « pas encore tranchée » — une valeur inconnue ne doit pas passer pour un jugement. */
function mapQualification(
  raw: string | null | undefined,
): MatchQualification | null {
  switch (raw) {
    case "CONFIRMED":
      return MatchQualification.CONFIRMED;
    case "DISCARDED":
      return MatchQualification.DISCARDED;
    default:
      return null;
  }
}

function mapOutcome(raw: string | null | undefined): ScreeningOutcome {
  switch (raw) {
    case "MATCH":
      return ScreeningOutcome.MATCH;
    case "NO_MATCH":
      return ScreeningOutcome.NO_MATCH;
    case "FAILED":
      return ScreeningOutcome.FAILED;
    case "NOT_SCREENED":
      return ScreeningOutcome.NOT_SCREENED;
    default:
      // Par défaut « à qualifier » : l'issue la plus prudente, celle qui laisse le dossier retenu.
      return ScreeningOutcome.TO_QUALIFY;
  }
}

export function mapMatchDtoToEntity(dto: QualifiableMatchDto): QualifiableMatch {
  return {
    matchId: dto.matchId,
    qualification: mapQualification(dto.qualification),
    qualifiedBy: dto.qualifiedBy ?? null,
    qualifiedAt: dto.qualifiedAt ?? null,
    motif: dto.motif ?? null,
    entityFullName: dto.entityFullName ?? null,
    entityDateOfBirth: dto.entityDateOfBirth ?? null,
    entityPlaceOfBirth: dto.entityPlaceOfBirth ?? null,
    entitySourceCountry: dto.entitySourceCountry ?? null,
    entityCategory: dto.entityCategory ?? null,
    politicallyExposed: dto.politicallyExposed ?? null,
    matchScore: dto.matchScore ?? null,
    sources: dto.sources ?? [],
    amlTypes: dto.amlTypes ?? [],
    dateOfBirthStatus: mapDiscriminant(dto.dateOfBirthStatus),
    placeOfBirthStatus: mapDiscriminant(dto.placeOfBirthStatus),
  };
}

export function mapMatchReviewDtoToEntity(dto: MatchReviewDto): MatchReview {
  return {
    businessKey: dto.businessKey,
    customerFullName: dto.customerFullName ?? null,
    customerDateOfBirth: dto.customerDateOfBirth ?? null,
    customerNationality: dto.customerNationality ?? null,
    customerPlaceOfBirth: dto.customerPlaceOfBirth ?? null,
    matches: (dto.matches ?? []).map(mapMatchDtoToEntity),
    outcome: mapOutcome(dto.outcome),
    remainingToQualify: dto.remainingToQualify ?? 0,
  };
}
